import {
  type FilledInputProps,
  type InputProps,
  type OutlinedInputProps,
  TextField,
} from "@mui/material";
import {
  SizingWrapper,
  type SizingWrapperStyle,
} from "@/components/Wrapper/SizingWrapper";
import React, { type ReactElement } from "react";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

interface IProps<T extends FieldValues> {
  control: Control<T> | undefined;
  name: FieldPath<T>;
  id?: string; // id未指定の場合はTextFieldの内部でuseId()が使用される
  label?: string;
  required?: boolean;
  autoFocus?: boolean;
  type?: React.InputHTMLAttributes<unknown>["type"];
  size?: SizingWrapperStyle;
  disabled?: boolean;
  inputProps?:
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | Partial<InputProps>;
  callbackOnBlur?: (value: React.FocusEvent<HTMLInputElement>) => void;
  callbackOnChange?: (
    value: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  center?: boolean;
}

/**
 * 数値を入力するテキストフィールド
 * RHF向けにコントロール化されています
 */
export const CTextField = <T extends FieldValues>(
  props: IProps<T>
): ReactElement => {
  const {
    autoFocus,
    disabled,
    id,
    inputProps,
    label,
    size,
    type,
    required,
    name,
    control,
    callbackOnBlur,
    callbackOnChange,
    center = false,
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onBlur, onChange, ref, value }, fieldState }) => {
        return (
          <SizingWrapper size={size} center={center}>
            <TextField
              id={id}
              onChange={(e) => {
                if (callbackOnChange) {
                  callbackOnChange(e);
                }
                if (type === "number" || type === "range") {
                  // UIからRHFへ値を送る際に数値へ変換する
                  // 空文字の場合の制御の都合、NumberとStringを行き来する
                  if (e.target.value === "") {
                    onChange("");
                    return;
                  }

                  const output = parseInt(e.target.value, 0);
                  onChange(isNaN(output) ? "0" : e.target.value);
                  return;
                }
                onChange(e.target.value);
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                if (callbackOnBlur) {
                  callbackOnBlur(event);
                }
                onBlur();
              }}
              required={required}
              autoFocus={autoFocus}
              type={type}
              ref={ref}
              disabled={disabled}
              error={Boolean(fieldState.error)}
              helperText={
                fieldState.error?.types
                  ? // criteriaMode: "all" の場合
                    // 複数のエラーメッセージを同時に表示するための処理
                    Object.values(fieldState.error.types)
                      .flat()
                      .map((msg, i, arr) => (
                        <React.Fragment key={i}>
                          {msg}
                          {i < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))
                  : // criteriaMode: "firstError"（デフォルト） の場合
                    fieldState.error?.message
              }
              label={label}
              value={value}
              fullWidth={true}
              InputProps={inputProps}
            />
          </SizingWrapper>
        );
      }}
    />
  );
};
